import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { useAuth } from 'contexts/auth';

import ComponentDemoPage from 'pages/ComponentDemo';

import CourseDashboardPage from 'pages/CourseDashboard';

import LoginPage from 'pages/Login';
import LTIIncompletePage from 'pages/LTI/Incomplete';
import RecoverPasswordPage from 'pages/RecoverPassword';

import SignUpPage from 'pages/SignUpPage';
import AccountSetting from 'pages/AccountSetting';
import ForgotPassword from 'pages/ForgotPasswordPage';
import ProfileSetUpPage from 'pages/ProfileSetUp';

import AllAccounts from 'pages/Admin/AllAccounts';
import CreateCoursePage from 'pages/Admin/CreateCourse';
import CreateAccountsPage from 'pages/Admin/CreateAccounts';

import ProfilePage from 'pages/Profile';
import ChatPage from 'pages/Chat';

import CourseSetUpPage from 'pages/CourseSetUp';
import GroupDetailPage from 'pages/GroupDetail';
import TaskManagerPage from 'pages/TaskManager';
import OverviewPage from 'pages/GroupOverView';
import Teams from 'pages/Teams';
import PeerEvaluationPage from 'pages/PeerEvaluation';
import SurveyPage from 'pages/Survey';

import ActivitiesPage from 'pages/Activities';
import DashboardPage from 'pages/Dashboard';
import FilesPage from 'pages/Files';
import TasksPage from 'pages/Tasks';

import Error404 from 'parts/Error/404';

const GrantPermission = ({ exact, path, component, roles, auth, user, userRole }) => {
    const exceptions = ['/components', '/accepted-invitation', '/lti-admin', '/lti-incomplete', '/lti-launch'];

    if (exceptions.includes(path)) {
        return <Route exact={exact} path={path} component={component} />;
    }

    if (auth && auth.role !== 'admin' && !auth.owner_account_id && user && !user.profile_set_up) {
        return <Route exact={exact} path={path} component={ProfileSetUpPage} />;
    } else if (auth && auth.role !== 'admin' && !auth.owner_account_id) {
        return <Route exact={exact} path={path} component={CourseDashboardPage} />;
    } else if (roles.length === 0 || roles?.includes(userRole)) {
        return <Route exact={exact} path={path} component={component} />;
    } else {
        return <Route component={Error404} />;
    }
};

const PasswordRoleBasedRouting = ({ popUpChatRef }) => {
    const { auth, currentUser } = useAuth();

    const getDefaultComponent = (auth) => {
        if (!auth) return LoginPage;
        switch (auth?.role) {
            case 'admin':
                return AllAccounts;
            case 'instructor':
            case 'student':
            default:
                return CourseDashboardPage;
        }
    };

    const noAuthRoutes = [
        { path: '/components', component: ComponentDemoPage, roles: [], exact: true },
        { path: '/', component: LoginPage, roles: [], exact: true },
        { path: '/login', component: LoginPage, roles: [], exact: true },
        { path: '/sign-up', component: SignUpPage, roles: [], exact: true },
        { path: '/forgot-password', component: ForgotPassword, roles: [], exact: true },
        { path: '/recoverpassword', component: RecoverPasswordPage, roles: [], exact: true },
    ];

    const routes = [
        { path: '/components', component: ComponentDemoPage, roles: [], exact: true },
        { path: '/', component: getDefaultComponent(auth), roles: [], exact: true },
        { path: '/accepted-invitation', component: AccountSetting, roles: [], exact: true },
        { path: '/lti-incomplete', component: LTIIncompletePage, roles: [], exact: true },
        { path: '/profile-setup', component: ProfileSetUpPage, roles: [], exact: true },
        { path: '/recoverpassword', component: RecoverPasswordPage, roles: [], exact: true },
        { path: '/sign-up', component: SignUpPage, roles: [], exact: true },
        { path: '/forgot-password', component: ForgotPassword, roles: [], exact: true },

        { path: '/allaccounts', component: AllAccounts, roles: ['admin'], exact: true },
        { path: '/create-course', component: CreateCoursePage, roles: ['admin'], exact: true },
        { path: '/create-user', component: CreateCoursePage, roles: ['admin'], exact: true },
        { path: '/invite-users', component: CreateAccountsPage, roles: ['admin'], exact: true },

        { path: '/profile', component: ProfilePage, roles: ['instructor', 'student'], exact: true },
        { path: '/messages', component: ChatPage, roles: ['instructor', 'student'], exact: true },
        { path: '/peer-evaluation', component: PeerEvaluationPage, roles: ['student', 'instructor'] },
        { path: '/peer-evaluation/:id', component: PeerEvaluationPage, roles: ['student', 'instructor'] },

        { path: '/course-set-up', component: CourseSetUpPage, roles: ['instructor'], exact: true },
        { path: '/task-manager', component: TaskManagerPage, roles: ['instructor'], exact: true },
        { path: '/overview', component: OverviewPage, roles: ['instructor'], exact: true },
        { path: '/overview/:id', component: GroupDetailPage, roles: ['instructor'], exact: true },
        { path: '/people', component: Teams, roles: ['instructor'], exact: true },
        { path: '/people/:tab', component: Teams, roles: ['instructor'], exact: true },

        { path: '/activities', component: ActivitiesPage, roles: ['student'], exact: true },
        { path: '/dashboard', component: DashboardPage, roles: ['student'], exact: true },
        { path: '/files', component: FilesPage, roles: ['student'], exact: true },
        { path: '/tasks', component: () => <TasksPage popUpChatRef={popUpChatRef} />, roles: ['student'], exact: true },
        {
            path: '/survey',
            component: SurveyPage,
            roles: ['student'],
            exact: true,
        },
    ];

    return (
        <Switch>
            {((auth ? routes : noAuthRoutes) || []).map((route) => {
                return (
                    <GrantPermission
                        key={route.path}
                        exact={route.exact}
                        path={route.path}
                        component={route.component}
                        roles={route.roles}
                        auth={auth}
                        user={currentUser}
                        userRole={auth?.role}
                    />
                );
            })}
            <Route component={Error404} />
        </Switch>
    );
};

export default PasswordRoleBasedRouting;
